// Approval logic for a group's member policies (OMK group retail).
//
// Two things vary per policy and are decided here rather than in the saga or
// the component, so both the "Approve All" and the per-row "Approve" paths go
// through exactly the same rules:
//
//   1. Whether the policy can be approved at all.
//   2. WHICH endpoint approves it — a policy carrying a sequenced
//      maker-checker stage is approved by recording a decision on that stage,
//      not by writing its status. This mirrors the fork PolicyHeader already
//      makes between MakerChecks and the single Approve button.
import { getCurrentPendingCheck } from '../../../../components/RecentPolicies/Components/makerCheckStage'

// A policy is only ever approved out of Un_Confirmed — the same status that
// gates the single Approve button on the policy-processing header.
export const APPROVABLE_STATUS = 'Un_Confirmed'

// Approving a policy moves it to Unpaid (it still owes its first premium);
// this matches getStatus() in the policy-processing container.
export const APPROVED_STATUS = 'Unpaid'

// Rejecting moves it to Rejected, from where the existing Re-Confirm /
// Discard actions on the policy-processing header take over.
export const REJECTED_STATUS = 'Rejected'

export function isPolicyApprovable(policy) {
  return !!policy && policy.status === APPROVABLE_STATUS
}

export function approvablePolicies(policies) {
  if (!Array.isArray(policies)) {
    return []
  }
  return policies.filter(isPolicyApprovable)
}

// GET /api/groups/{guid}/policies answers with group MEMBERSHIP records, not
// policies. Verified against staging: each row is
//   { guid, policy_guid, policy_number, group_guid, policy: {...}, ... }
// where `guid` is the MEMBERSHIP's own guid and the policy — the thing that
// carries `status` and the guid an approval must target — is nested under
// `policy`. Approving `row.guid` would address the wrong entity entirely, so
// every row is unwrapped to its nested policy before anything else sees it.
export function toPolicy(row) {
  if (!row || typeof row !== 'object') {
    return null
  }
  if (row.policy && typeof row.policy === 'object') {
    return row.policy
  }
  // Some membership rows carry no nested policy (seen on staging: a whole
  // group of them). Keep the row visible — dropping it would silently
  // disagree with the reported total — but re-point guid at policy_guid so
  // nothing downstream can ever address the membership record by mistake.
  // These rows have no status, so they are not approvable either way.
  if (row.policy_guid) {
    return Object.assign({}, row, { guid: row.policy_guid })
  }
  // Already a policy (or a shape we don't recognise) — pass it through rather
  // than dropping the row.
  return row
}

// Envelope: staging returns { data, total, page, limit }. The other wrappers
// are accepted too so an envelope change surfaces as a visible list rather
// than an empty table.
export function readPolicyList(payload) {
  const rows = readEnvelopeRows(payload)
  return rows.map(toPolicy).filter(Boolean)
}

function readEnvelopeRows(payload) {
  if (Array.isArray(payload)) {
    return payload
  }
  if (!payload || typeof payload !== 'object') {
    return []
  }
  const candidates = [payload.data, payload.policies, payload.results]
  for (let i = 0; i < candidates.length; i++) {
    if (Array.isArray(candidates[i])) {
      return candidates[i]
    }
  }
  return []
}

// Total count for pagination, wherever the envelope happens to carry it.
export function readPolicyTotal(payload, fallbackLength) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const totals = [payload.total, payload.count, payload.total_count]
    for (let i = 0; i < totals.length; i++) {
      if (typeof totals[i] === 'number') {
        return totals[i]
      }
    }
  }
  return fallbackLength || 0
}

// How to approve one policy. `kind` tells the saga which call to make; the
// body is the exact payload that call expects.
export function approvalPlanFor(policy) {
  const pendingCheck = getCurrentPendingCheck(policy)
  if (pendingCheck) {
    return {
      kind: 'maker-check',
      guid: policy.guid,
      body: { decision: 'Approved', check_guid: pendingCheck.guid }
    }
  }
  return {
    kind: 'status',
    policy_guid: policy.guid,
    body: { status: APPROVED_STATUS }
  }
}

// How to reject one policy — the same maker-check-vs-status fork as approval.
//
// A reason is mandatory: the decisions endpoint rejects an empty one outright,
// and on the status path the reason is the only record of WHY a policy was
// turned down, so the caller must always supply it.
//
// On the status path additional_details is carried over rather than cleared.
// The backend replaces the object wholesale with whatever is sent, and the
// single-policy reject flow's default of {} is known to wipe product data
// (tier, coverage_type and friends) — losing that across a bulk run would be
// far worse than across one policy.
export function rejectionPlanFor(policy, reason) {
  const pendingCheck = getCurrentPendingCheck(policy)
  if (pendingCheck) {
    return {
      kind: 'maker-check',
      guid: policy.guid,
      body: { decision: 'Rejected', reason, check_guid: pendingCheck.guid }
    }
  }
  return {
    kind: 'status',
    policy_guid: policy.guid,
    body: {
      status: REJECTED_STATUS,
      additional_details: Object.assign({}, policy.additional_details || {})
    }
  }
}

// Stable identity for a row's result, so the UI can match an outcome back to
// the policy it belongs to even when policy_number is missing.
export function policyLabel(policy) {
  if (!policy) {
    return 'Unknown policy'
  }
  return policy.policy_number || policy.guid || 'Unknown policy'
}

// Roll the per-policy outcomes into the one-line summary shown when a bulk run
// finishes. Kept here so the wording is identical wherever it is displayed.
export function summariseResults(results) {
  const list = Array.isArray(results) ? results : []
  const approved = list.filter(function(result) {
    return result && result.ok
  }).length
  const failed = list.length - approved
  return { total: list.length, approved, failed }
}

// `decision` is 'reject' for a rejection run, anything else for approval — the
// same wording would otherwise tell an operator their rejections were approved.
export function summaryMessage(results, decision) {
  const summary = summariseResults(results)
  const verb = decision === 'reject' ? 'rejected' : 'approved'
  if (!summary.total) {
    return `No policies were ${verb}.`
  }
  if (!summary.failed) {
    return `All ${summary.approved} policies were ${verb}.`
  }
  if (!summary.approved) {
    return `None of the ${summary.total} policies could be ${verb}.`
  }
  return (
    `${summary.approved} of ${summary.total} policies were ${verb}; ` +
    `${summary.failed} failed.`
  )
}
